import React, { useState, useEffect } from "react";
import ThemedButton from "../Shared/ThemedButton";
import { isDarkMode } from "../../utility/themeVars";
import { formatDateTime } from "../../utility/bookingUtils";

const FloatingRescheduleButton = ({
  bookingPage,
  selectedDate = null,
  selectedTime = null,
  isAvailableSlot = false,
  customTimeRequested = false,
  onSubmitReschedule,
  disabled = false,
  isSubmitting = false,
  primaryColor = "blue-600",
  themeMode = "light",
  rescheduleWorkflowRef = null,
}) => {
  const isDark = isDarkMode(themeMode);
  const [isVisible, setIsVisible] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const next = Boolean(selectedDate && selectedTime);
    setShouldShow((prev) => (prev === next ? prev : next));
  }, [selectedDate, selectedTime]);

  useEffect(() => {
    if (!rescheduleWorkflowRef?.current || !shouldShow) {
      setIsVisible(shouldShow);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(!entry.isIntersecting && shouldShow);
      },
      {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      }
    );

    observer.observe(rescheduleWorkflowRef.current);

    return () => {
      observer.disconnect();
    };
  }, [rescheduleWorkflowRef, shouldShow]);

  // Helper function to determine if appointment requires approval
  const requiresApproval = () => {
    return (
      customTimeRequested ||
      !isAvailableSlot ||
      (bookingPage && bookingPage.autoApproval === false)
    );
  };

  // Format the new appointment time
  const formatNewTime = () => {
    if (!selectedDate || !selectedTime) return "—";
    return formatDateTime(selectedDate, selectedTime);
  };

  // Check if reschedule can be submitted
  const canSubmit = selectedDate && selectedTime && !disabled && !isSubmitting;

  if (!isVisible) return null;

  return (
    <div className="block lg:hidden">
      {/* Floating Button Container */}
      <div
        className={`fixed left-4 right-4 z-50 transform transition-all duration-300 ease-out ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
        }`}
        style={{ bottom: 'max(1rem, env(safe-area-inset-bottom, 1rem))' }}
      >
        <div className="bg-themed-card border-themed rounded-card shadow-lift border">
          <div className="p-4">
            {/* Summary Info */}
            <div className="mb-3">
              <div className="text-sm text-themed-secondary tabular">
                {formatNewTime()}
              </div>
            </div>

            {/* Approval Status Indicator */}
            {selectedDate && selectedTime && (
              <div className="mb-3">
                {requiresApproval() ? (
                  <div
                    className={`${
                      isDark
                        ? "bg-signal-amber-500/20 border-signal-amber-500"
                        : "bg-signal-amber-50 border-signal-amber-100"
                    } border rounded-card p-2`}
                  >
                    <div className="flex items-start space-x-2">
                      <svg
                        aria-hidden="true"
                        className={`w-4 h-4 ${
                          isDark ? "text-signal-amber-100" : "text-signal-amber-600"
                        } mt-0.5 flex-shrink-0`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p
                        className={`text-xs font-medium ${
                          isDark ? "text-signal-amber-100" : "text-signal-amber-600"
                        }`}
                      >
                        Needs confirmation
                      </p>
                    </div>
                  </div>
                ) : (
                  <div
                    className={`${
                      isDark
                        ? "bg-signal-green-500/20 border-signal-green-500"
                        : "bg-signal-green-50 border-signal-green-100"
                    } border rounded-card p-2`}
                  >
                    <div className="flex items-start space-x-2">
                      <svg
                        aria-hidden="true"
                        className={`w-4 h-4 ${
                          isDark ? "text-signal-green-100" : "text-signal-green-600"
                        } mt-0.5 flex-shrink-0`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                      <p
                        className={`text-xs font-medium ${
                          isDark ? "text-signal-green-100" : "text-signal-green-600"
                        }`}
                      >
                        Your new time is available
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Continue Button */}
            <ThemedButton
              onClick={onSubmitReschedule}
              primaryColor={primaryColor}
              themeMode={themeMode}
              size="large"
              className="w-full"
              disabled={!canSubmit}
            >
              {isSubmitting
                ? "Submitting..."
                : requiresApproval()
                ? "Submit reschedule request"
                : "Confirm reschedule"}
            </ThemedButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FloatingRescheduleButton;
